import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import StatusBadge from "@/components/common/StatusBadge";
import { toast } from "sonner";
import { useAuth, can } from "@/contexts/AuthContext";

const STATUS_FLOW = ["pending_verification", "approved", "paid", "rejected"];

export default function Milestones() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter) params.status = filter;
      const { data } = await api.get("/milestones", { params });
      setRows(data);
    } catch (e) { toast.error("Failed"); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const act = async (m, status) => {
    try {
      await api.post(`/milestones/${m.id}/action`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 md:p-8 max-w-[1600px] mx-auto">
      <PageHeader title="Milestones" subtitle="Cumulative rewards: 10 lot = USD 100 · 20 lot = USD 200 · 30 lot = Bali Trip." breadcrumbs="Rewards / Milestones" testId="milestones-page" />

      <div className="flex gap-2 mb-4">
        {["", "pending_verification", "approved", "paid", "rejected"].map((s) => (
          <button
            key={s || "all"}
            data-testid={`milestone-filter-${s || "all"}`}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 text-xs rounded-md border ${filter === s ? "bg-blue-500/10 border-blue-500 text-blue-300" : "border-white/10 text-gray-400 hover:text-white"}`}
          >
            {s ? s.replace("_", " ") : "All"}
          </button>
        ))}
      </div>

      <div className="bg-[#111827] border border-white/10 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-[#1F2937]/50 text-left text-[10px] uppercase tracking-widest text-gray-500">
                <th className="px-4 py-3">Participant</th>
                <th className="px-4 py-3 text-center">Milestone</th>
                <th className="px-4 py-3 text-right">Reward</th>
                <th className="px-4 py-3 text-right">Total Lot</th>
                <th className="px-4 py-3">Achieved</th>
                <th className="px-4 py-3">Status</th>
                {can(user, "approve_payout") && <th className="px-4 py-3 text-right">Action</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={7} className="p-8 text-center text-gray-500">Loading…</td></tr> :
                rows.length === 0 ? <tr><td colSpan={7} className="p-8 text-center text-gray-500">No milestones</td></tr> :
                rows.map((m) => (
                  <tr key={m.id} data-testid={`milestone-row-${m.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <p className="text-white font-medium">{m.participant_name}</p>
                      <p className="text-xs font-mono text-gray-500">{m.account_number}</p>
                    </td>
                    <td className="px-4 py-3 text-center font-mono text-orange-300 font-bold">{m.milestone_lot}</td>
                    <td className="px-4 py-3 text-right font-mono text-emerald-400">${m.reward_amount.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-mono text-white">{(m.total_valid_lot || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-xs font-mono text-gray-400">{(m.achieved_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-3"><StatusBadge status={m.status} /></td>
                    {can(user, "approve_payout") && (
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-1">
                          {m.status === "pending_verification" && (
                            <button data-testid={`milestone-approve-${m.id}`} onClick={() => act(m, "approved")} className="px-2 py-1 text-xs bg-blue-500/10 text-blue-300 border border-blue-500/30 rounded-md hover:bg-blue-500/20">Approve</button>
                          )}
                          {m.status === "approved" && (
                            <button data-testid={`milestone-pay-${m.id}`} onClick={() => act(m, "paid")} className="px-2 py-1 text-xs bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 rounded-md hover:bg-emerald-500/20">Mark Paid</button>
                          )}
                          {m.status !== "rejected" && m.status !== "paid" && (
                            <button data-testid={`milestone-reject-${m.id}`} onClick={() => act(m, "rejected")} className="px-2 py-1 text-xs bg-red-500/10 text-red-300 border border-red-500/30 rounded-md hover:bg-red-500/20">Reject</button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
